import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Bot } from 'lucide-react';

const PALETTE = ['#6366f1', '#8b5cf6', '#0ea5e9', '#f59e0b', '#f43f5e', '#10b981'];
const PANEL_W = 308;

// Expandable "why?" — opens a floating popover (fixed-positioned so it isn't
// clipped by narrow table columns / card overflow) explaining the risk score.
export default function ReasoningTrace({ risk }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({
      top: Math.round(r.bottom + 6),
      left: Math.round(Math.min(r.left, window.innerWidth - PANEL_W - 16)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    const close = (e) => { if (!panelRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open, place]);

  if (!risk || risk.score == null) return null;
  const factors = risk.factors || [];
  const total = factors.reduce((s, f) => s + f.points, 0) || 1;
  const levelTone = risk.level === 'high' ? 'text-rose-600' : risk.level === 'medium' ? 'text-amber-600' : 'text-emerald-600';

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex items-center gap-1 text-[11px] text-brand-600 font-medium hover:text-brand-700 whitespace-nowrap"
      >
        why? <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: PANEL_W }}
          className="z-50 rounded-xl border border-violet-200 bg-white shadow-xl shadow-slate-900/10 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3.5 py-2.5 bg-violet-50/60 border-b border-violet-100">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700"><Bot className="h-3.5 w-3.5" />Risk Agent</span>
            <span className="text-xs text-slate-500">score <b className={`tnum ${levelTone}`}>{risk.score}</b> · {risk.level}</span>
          </div>

          <div className="p-3.5">
            {factors.length === 0 ? (
              <div className="text-xs text-slate-600">No risk factors — in-network, active coverage, no exceptions.</div>
            ) : (
              <>
                {/* Proportional bar */}
                <div className="flex h-2 w-full rounded-full overflow-hidden mb-3 bg-slate-100">
                  {factors.map((f, i) => (
                    <div key={i} style={{ width: `${(f.points / total) * 100}%`, background: PALETTE[i % PALETTE.length] }} title={`${f.label} +${f.points}`} />
                  ))}
                </div>

                {/* Factor rows */}
                <ul className="space-y-2">
                  {factors.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] text-slate-800 leading-tight">{f.label}</div>
                        <div className="text-[10px] text-slate-400 leading-tight">{f.source}</div>
                      </div>
                      <span className="text-xs font-semibold tnum text-slate-700 shrink-0">+{f.points}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Total risk score</span>
                  <span className={`text-sm font-bold tnum ${levelTone}`}>{risk.score}<span className="text-slate-400 font-normal">/100</span></span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
